import {
  assignBenefitOptin,
  getBenefitAvailableProducts,
  getBenefitDetail,
  getBenefitOptin,
  getBenefitsList,
} from '@/common/integrations/menhir/employee/benefits/benefits.service';
import {
  IBenefit,
  IBenefitDetail,
  IBenefitOptin,
  IMenhirBenefitSlug,
} from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { useGetFeatures } from '@/common/integrations/menhir/employee/features/features.hooks';
import { TOrderCategorySlug } from '@/common/integrations/menhir/employee/orders/orders.types';
import { MENHIR_ENDPOINTS } from '@/common/integrations/menhir/menhir.endpoints';
import { ICatalogProduct } from '@/common/types/catalog';
import { TMoney } from '@coverflex-tech/hypernova-utils';
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

export function useGetBenefits(
  options?: UseQueryOptions<IBenefit[], any, IBenefit[]>
) {
  const { data: benefits, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.BENEFITS.ROOT],
    queryFn: getBenefitsList,
    ...options,
  });

  return { benefits, ...rest };
}

export function useGetBenefitsOnly(
  options?: UseQueryOptions<IBenefit[], any, IBenefit[]>
) {
  const { benefits, ...rest } = useGetBenefits(options);
  const benefitsList = benefits?.filter(
    (benefit) => benefit.slug !== 'meals'
  ) as IBenefit<IMenhirBenefitSlug>[];

  return { benefits: benefitsList, ...rest };
}

export function useGetBenefitDetail<T extends IMenhirBenefitSlug>(
  { slug }: { slug: T },
  options?: UseQueryOptions<
    IBenefitDetail<T>,
    unknown,
    IBenefitDetail<T>,
    QueryKey
  >
) {
  const { data: benefit, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.BENEFITS.DETAIL, slug],
    queryFn: () => getBenefitDetail(slug),
    ...options,
  });

  return { benefit, ...rest };
}

export function useGetBenefitOptin(
  options?: UseQueryOptions<IBenefitOptin, any, IBenefitOptin>
) {
  const {
    features: { FF_BENEFITS_OPTIN },
    isInitialLoading: loadingFeatures,
  } = useGetFeatures();
  const {
    data: optIn,
    isInitialLoading,
    ...rest
  } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.BENEFITS.OPTIN],
    queryFn: getBenefitOptin,
    enabled: FF_BENEFITS_OPTIN,
    ...options,
  });
  return {
    optIn,
    isInitialLoading: loadingFeatures || isInitialLoading,
    ...rest,
  };
}

export function useAssignBenefitOptin(
  options?: UseMutationOptions<string, any, TMoney>
) {
  return useMutation({
    mutationFn: assignBenefitOptin,
    ...options,
  });
}

export function useGetBenefitProducts<
  T extends ICatalogProduct<TOrderCategorySlug>,
>(
  { slug }: { slug: TOrderCategorySlug },
  options?: UseQueryOptions<T[], any, T[]>
) {
  const { data: products, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.BENEFITS.PRODUCTS, slug],
    queryFn: () => getBenefitAvailableProducts<TOrderCategorySlug, T>(slug),
    ...options,
  });

  return { products, ...rest };
}
